import { useRef, useState, useEffect } from "react";
import { useMathJax } from "../../../hooks/useMathJax";
import { useCaptionHighlight } from "../../../hooks/useCaptionHighlight";
import {
  processText,
  svgToDataUrl,
  parseArrowKey,
} from "../../../utils/learnUtils";
import { useLearnCtx } from ".././LearnPage";
import SelectionToolbar from "./SelectionToolbar";

/**
 * Renders EDCOMP_FIGURE with expand button.
 * @param {{ item: object }} props
 */
export default function FigureComp({ item }) {
  const { setLightbox, openBookmarkModal } = useLearnCtx();
  const { text: rawCaption, image, width } = item.data ?? {};
  const caption = processText(rawCaption ?? "");
  const captionRef = useRef(null);

  const {
    onTypesetRef,
    toolbarPos,
    toolbarStyle,
    handleHighlight,
    handleRemoveHighlight,
    copyDone,
    copySelection,
    preventDeselect,
  } = useCaptionHighlight(captionRef, item._id);

  useMathJax(captionRef, [rawCaption], onTypesetRef);

  const [activeKeys, setActiveKeys] = useState(new Set());

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    const onClick = (e) => {
      const btn = e.target.closest(".ct__fig-ptr-btn");
      if (!btn) return;
      e.preventDefault();
      const key = btn.dataset.arrows;
      setActiveKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    const el = captionRef.current;
    if (!el) return;
    el.querySelectorAll(".ct__fig-ptr-btn").forEach((btn) => {
      btn.classList.toggle(
        "ct__fig-ptr-btn--active",
        activeKeys.has(btn.dataset.arrows),
      );
    });
  }, [activeKeys]);

  const visibleArrows = [...activeKeys].flatMap(parseArrowKey);

  const handleExpand = () => setLightbox({ svg: image, caption });
  const isFullWidth = width === "100%";

  return (
    <figure
      id={"fig:" + item._id}
      className={`ct__figure${isFullWidth ? " ct__figure--full" : " ct__figure--partial"}`}
    >
      <div className="ct__figure-img-wrap" style={{ width: width ?? "100%" }}>
        {image && (
          <img
            src={svgToDataUrl(image)}
            alt="Figure"
            className="ct__figure-img"
          />
        )}
        {visibleArrows.map(({ x, y }, i) => (
          <PointerArrow key={i} x={x} y={y} />
        ))}
      </div>
      <button
        className="ct__figure-expand"
        onClick={handleExpand}
        aria-label="Expand figure"
        title="Expand figure"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path
            d="M2 2h4M2 2v4M12 2H8M12 2v4M2 12h4M2 12V8M12 12H8M12 12V8"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
      {rawCaption && (
        <figcaption
          ref={captionRef}
          className="ct__figure-caption"
          dangerouslySetInnerHTML={{ __html: caption }}
        />
      )}

      {toolbarPos && (
        <SelectionToolbar
          style={toolbarStyle}
          onCopy={copySelection}
          copyDone={copyDone}
          onPointerDown={preventDeselect}
          onBookmark={() =>
            openBookmarkModal(item._id, item.title, "fig:" + item._id)
          }
          onHighlight={handleHighlight}
          onRemoveHighlight={handleRemoveHighlight}
        />
      )}
    </figure>
  );
}

function PointerArrow({ x, y }) {
  return (
    <svg
      className="ct__fig-ptr-arrow"
      style={{ left: `${x}%`, top: `${y}%` }}
      width="16"
      height="28"
      viewBox="0 0 16 28"
      aria-hidden="true"
    >
      <line
        x1="8"
        y1="0"
        x2="8"
        y2="17"
        stroke="white"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <polygon points="8,28 0,14 16,14" fill="white" />
      <line
        x1="8"
        y1="1"
        x2="8"
        y2="17"
        stroke="var(--blue-primary)"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <polygon points="8,27 2,15 14,15" fill="var(--blue-primary)" />
    </svg>
  );
}
